/**
 * Customer-facing Registered Agent addresses (Geaux File LLC)
 *
 * Single source of truth for any RA address shown to customers, prospects,
 * or the public: portal pages, customer emails, generated documents, chat,
 * and API examples.
 *
 * Rules (EAI-136, GXF-16):
 * - Geaux File LLC serves LA/MS/AL only.
 * - The representative is always the entity, never an individual.
 * - Only official commercial addresses are published. Staff residences are
 *   never customer-facing. A state without a commercial address fails closed:
 *   no street address, only "on file" copy.
 * - Seminary, MS is the only official commercial RA address today. It must
 *   NOT be substituted for LA or AL — an RA address has to be in-state.
 *
 * Pure module with no dependencies so it is safe for browser bundles.
 *
 * @module @geauxos/core/ra/public-address
 */

/** Geaux File LLC RA footprint (GXF-16). */
export const GFL_RA_STATE_CODES = ['LA', 'MS', 'AL'] as const;
export type GflRAStateCode = (typeof GFL_RA_STATE_CODES)[number];

/** Customer-facing RA representative — always the entity. */
export const GFL_RA_REPRESENTATIVE = 'Geaux File LLC';

export interface CommercialRAAddress {
  street1: string;
  city: string;
  stateCode: GflRAStateCode;
  zip: string;
}

/**
 * Official commercial RA addresses. Add LA/AL here only once an official
 * in-state commercial address is approved — never a staff residence.
 */
export const GFL_COMMERCIAL_RA_ADDRESSES: Readonly<Partial<Record<GflRAStateCode, CommercialRAAddress>>> = {
  MS: {
    street1: '423 Walter Lott Rd',
    city: 'Seminary',
    stateCode: 'MS',
    zip: '39479-4447',
  },
};

const STATE_NAMES: Record<GflRAStateCode, string> = {
  LA: 'Louisiana',
  MS: 'Mississippi',
  AL: 'Alabama',
};

/**
 *
 */
export function isGflRAState(stateCode: string | null | undefined): stateCode is GflRAStateCode {
  return !!stateCode && (GFL_RA_STATE_CODES as readonly string[]).includes(stateCode);
}

/** Structured commercial address for a state, or undefined when none is published. */
export function getCommercialRAAddress(stateCode: string | null | undefined): CommercialRAAddress | undefined {
  return isGflRAState(stateCode) ? GFL_COMMERCIAL_RA_ADDRESSES[stateCode] : undefined;
}

/** One-line commercial address ("423 Walter Lott Rd, Seminary, MS 39479-4447") or null. */
export function formatCommercialRAAddress(stateCode: string | null | undefined): string | null {
  const addr = getCommercialRAAddress(stateCode);
  return addr ? `${addr.street1}, ${addr.city}, ${addr.stateCode} ${addr.zip}` : null;
}

/**
 * Text safe to place wherever a customer-facing RA address is expected:
 * the commercial address when one exists, otherwise a reference to the
 * address on file with the state.
 */
export function getCustomerFacingRAAddressText(stateCode: string | null | undefined): string {
  const formatted = formatCommercialRAAddress(stateCode);
  if (formatted) return formatted;
  const stateName = isGflRAState(stateCode) ? STATE_NAMES[stateCode] : 'the state';
  return `Registered office address on file with the ${stateName} Secretary of State`;
}
